import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import Breadcrumbs from '../../components/ui/breadcrumbs';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import { Users, Trash2, Download, Search, Building, Globe } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';

const AdminWaitlist = () => {
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredWaitlist, setFilteredWaitlist] = useState([]);

  useEffect(() => {
    fetchWaitlist();
  }, []);

  useEffect(() => {
    // Filter waitlist based on search term
    const filtered = waitlist.filter(entry =>
      entry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.company && entry.company.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredWaitlist(filtered);
  }, [waitlist, searchTerm]);

  const fetchWaitlist = async () => {
    try {
      const response = await axios.get('/admin/waitlist');

      // Handle different response formats
      let waitlistData = [];
      if (response.data?.waitlist?.data) {
        // Paginated response
        waitlistData = response.data.waitlist.data;
      } else if (response.data?.waitlist) {
        // Direct array response
        waitlistData = response.data.waitlist;
      } else if (response.data?.data) {
        // Alternative structure
        waitlistData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Direct array
        waitlistData = response.data;
      }

      setWaitlist(Array.isArray(waitlistData) ? waitlistData : []);
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
      toast.error('Failed to fetch waitlist');
      setWaitlist([]); // Ensure waitlist is always an array
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (id) => {
    if (window.confirm('Are you sure you want to delete this waitlist entry?')) {
      try {
        await axios.delete(`/admin/waitlist/${id}`);
        toast.success('Waitlist entry deleted successfully');
        fetchWaitlist();
      } catch (error) {
        toast.error('Failed to delete waitlist entry');
      }
    }
  };

  const exportWaitlist = async () => {
    try {
      const response = await axios.get('/admin/waitlist/export');
      const blob = new Blob([JSON.stringify(response.data.data, null, 2)], {
        type: 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `waitlist-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Waitlist exported successfully');
    } catch (error) {
      toast.error('Failed to export waitlist');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading waitlist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6"><Breadcrumbs /></div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Waitlist Management</h1>
        <Button
          onClick={exportWaitlist}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Export Waitlist
        </Button>
      </div>

      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <div className="text-2xl font-bold text-blue-600">{waitlist.length}</div>
                <p className="text-gray-600">Total Signups</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Building className="h-8 w-8 text-green-600" />
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {waitlist.filter(entry => entry.company).length}
                </div>
                <p className="text-gray-600">With Company</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Globe className="h-8 w-8 text-purple-600" />
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {waitlist.filter(entry => entry.website).length}
                </div>
                <p className="text-gray-600">With Website</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Waitlist Entries */}
      <Card>
        <CardHeader>
          <CardTitle>
            Waitlist Entries ({filteredWaitlist.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredWaitlist.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{entry.name}</div>
                      <div className="text-sm text-gray-600">{entry.email}</div>
                      {entry.company && (
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Building className="h-3 w-3" />
                          {entry.company}
                        </div>
                      )}
                      {entry.website && (
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          <a
                            href={entry.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {entry.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-sm text-gray-500">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(entry.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteEntry(entry.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {filteredWaitlist.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? 'No entries match your search' : 'No waitlist entries found'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminWaitlist;
